import {FC, useState} from 'react'
import {MenuProvider, MenuButton} from '@ariakit/react'
import {Button} from '@components/ui/button/Button.tsx'
import {DotsHorizontalIcon, Download01Icon, EyeIcon, Trash01Icon} from '@components/ui/icon/Icon.tsx'
import {Menu, MenuGroup, MenuItem} from '@components/ui/menu-atoms/MenuAtoms.tsx'
import {Link} from 'react-router-dom'
import {useTranslation} from '@/translations/i18n.tsx'
import {useTheme} from 'styled-components'
import {TreatmentRequestsColumn, TreatmentRequestSmall} from '@/features/treatment-requests/types.ts'
import {httpExportTreatmentRequests} from '@/features/treatment-requests/services/treatment-requests.http.ts'
import {ChangeStatusMenu} from '@/features/treatment-requests/components/change-status-menu/ChangeStatusMenu.tsx'
import {DeleteTreatmentRequestModal} from '@/features/treatment-requests/components/delete-treatment-request-modal/DeleteTreatmentRequestModal.tsx'
import {columnVisibilitySearchParamsToPayload, filtersParamToPayload, sortParamToPayload} from '@/utilities/helpers'
import {treatmentRequestTableToHiddenColumns, useTreatmentRequestSearchParams} from '../../utils'
import {usePermissions} from '@/features/auth/store/store'
import {ExportModal} from '@/components/commons/export-modal/ExportModal'

export const RowMenu: FC<{
    tableStatus?: Exclude<TreatmentRequestSmall['status'], 'deprecated'>
    treatmentRequest: TreatmentRequestSmall
    onDeleteSuccess: () => void
}> = ({tableStatus, treatmentRequest, onDeleteSuccess}) => {
    const {t} = useTranslation()
    const {searchParams} = useTreatmentRequestSearchParams()
    const theme = useTheme()
    const [isOpen, setOpen] = useState(false)
    const [isDeleteTreatmentRequestModalOpen, setIsDeleteTreatmentRequestModalOpen] = useState(false)
    const permissions = usePermissions()
    const columnVisibility =
        columnVisibilitySearchParamsToPayload(TreatmentRequestsColumn, searchParams.columnVisibility) ??
        Object.fromEntries(TreatmentRequestsColumn.options.map(column => [column, true]))

    return (
        <>
            <MenuProvider open={isOpen} setOpen={setOpen}>
                <MenuButton
                    render={
                        <Button
                            className={isOpen ? undefined : 'show-on-hover'}
                            size="xs"
                            shape="square"
                            variant="secondary"
                            onClick={event => event.stopPropagation()}
                        >
                            <DotsHorizontalIcon />
                        </Button>
                    }
                />
                <Menu width="200px" onClick={event => event.stopPropagation()}>
                    <MenuGroup>
                        <MenuItem render={<Link to={treatmentRequest.id} />}>
                            <EyeIcon />
                            {t('commons:details')}
                        </MenuItem>
                        {permissions?.requests?.write && (
                            <>
                                <ChangeStatusMenu
                                    patientIds={[treatmentRequest.patientId]}
                                    trigger={<MenuItem />}
                                    selectedTreatmentRequestsCount={1}
                                    unselectedTreatmentRequestsIds={[]}
                                    selectedTreatmentRequestsIds={[treatmentRequest.id]}
                                    selectedTreatmentRequests={[treatmentRequest]}
                                />
                                <ExportModal
                                    trigger={
                                        <MenuItem hideOnClick={false}>
                                            <Download01Icon />
                                            {t('commons:export_option')}
                                        </MenuItem>
                                    }
                                    fileName="Richieste di trattamento"
                                    promise={({extension, mode}) =>
                                        httpExportTreatmentRequests({
                                            payload: {
                                                selectedTreatmentRequestIds: [treatmentRequest.id],
                                                exceptedTreatmentRequestIds: [],
                                                type: extension,
                                                filters: filtersParamToPayload(searchParams.filters)?.filters,
                                                fields:
                                                    mode == 'view'
                                                        ? TreatmentRequestsColumn.exclude([
                                                              'select',
                                                              'moreActions'
                                                          ]).options.filter(
                                                              column =>
                                                                  columnVisibility[column] &&
                                                                  !treatmentRequestTableToHiddenColumns[
                                                                      tableStatus ?? 'all'
                                                                  ].some(hiddenColumn => hiddenColumn == column)
                                                          )
                                                        : TreatmentRequestsColumn.exclude([
                                                              'select',
                                                              'moreActions'
                                                          ]).options.filter(
                                                              column =>
                                                                  !treatmentRequestTableToHiddenColumns[
                                                                      tableStatus ?? 'all'
                                                                  ].some(hiddenColumn => hiddenColumn == column)
                                                          ),
                                                sorts: sortParamToPayload(searchParams.sorts)
                                            }
                                        })
                                    }
                                    selectedCount={1}
                                    allowedExtensions={['xlsx', 'xls', 'csv']}
                                />
                            </>
                        )}
                    </MenuGroup>
                    {permissions?.requests?.write && (
                        <MenuGroup>
                            <MenuItem
                                onClick={() => setIsDeleteTreatmentRequestModalOpen(true)}
                                style={{color: theme.palette.danger['600']}}
                            >
                                <Trash01Icon style={{fill: theme.palette.danger['600']}} />
                                {t('commons:delete')}
                            </MenuItem>
                        </MenuGroup>
                    )}
                </Menu>
            </MenuProvider>
            {isDeleteTreatmentRequestModalOpen && (
                <DeleteTreatmentRequestModal
                    treatmentRequest={treatmentRequest}
                    onClose={() => setIsDeleteTreatmentRequestModalOpen(false)}
                    onSuccess={onDeleteSuccess}
                />
            )}
        </>
    )
}
